import { BoardPurchaseInterface } from '../interfaces/board-purchase.interface';
import { CommonEntityDto } from '../../common/dto/common-entity.dto';
import { SupplierDto } from '../../supplier/dto/supplier.dto';
import { JobDto } from '../../job/dto/job.dto';

import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';

import {
  IsBoolean,
  IsISO8601,
  IsNumber,
  IsString,
  IsUUID,
  MaxLength,
  MinLength,
  ValidateNested,
} from 'class-validator';

import { Exclude, Expose, Type } from 'class-transformer';

import {
  BOARD_PURCHASE_ORDER_NUMBER_MAX_LENGTH,
  BOARD_PURCHASE_ORDER_NUMBER_MIN_LENGTH,
} from '../constants/board-purchase-validation.constants';
import { BoardFloorDto } from '../../board-floor/dto/board-floor.dto';
import { MaterialDto } from '../../material/dto/material.dto';
import { GeographyPointDto } from '../../common/dto/geography-point.dto';
import { BoardPurchaseFloorDto } from '../../board-purchase-floor/dto/board-purchase-floor.dto';

@Exclude()
export class BoardPurchaseDto
  extends CommonEntityDto
  implements BoardPurchaseInterface
{
  @ApiProperty({
    title: 'PO Number',
    description: 'The PO Number',
    minLength: BOARD_PURCHASE_ORDER_NUMBER_MIN_LENGTH,
    maxLength: BOARD_PURCHASE_ORDER_NUMBER_MAX_LENGTH,
  })
  @IsString()
  @MinLength(BOARD_PURCHASE_ORDER_NUMBER_MIN_LENGTH)
  @MaxLength(BOARD_PURCHASE_ORDER_NUMBER_MAX_LENGTH)
  @Expose()
  orderNumber!: string;

  @ApiProperty({
    title: 'Order Label',
    description: 'The Order Label',
    nullable: true,
  })
  @IsString()
  @Expose()
  orderLabel!: string | null;

  @ApiProperty({
    title: 'Notes for Supplier',
    description: 'Notes for Supplier',
    nullable: true,
  })
  @IsString()
  @Expose()
  notesForSupplier!: string;

  @ApiProperty({
    format: 'date',
    title: 'Date Expected',
    description: 'The date the order is expected to be delivered.',
  })
  @IsISO8601({ strict: true })
  @Expose()
  originalDateExpected!: Date;

  @ApiProperty({
    format: 'date',
    title: 'Date Expected Override',
    description: 'The date the order is expected to be delivered (override).',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateExpected!: Date;

  @ApiProperty({
    type: 'boolean',
    title: 'Labor Only?',
    description:
      'Is the board purchase labor only? ' +
      'If this is set to true, then no supplier is allowed.',
    default: false,
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  laborOnly!: boolean;

  @ApiProperty({
    type: 'boolean',
    title: 'Is Replacement?',
    description:
      'Is the board purchase a replacement? ' +
      'If this is set to true, then board totals are not included in labor reporting.',
    default: false,
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  isReplacement!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'Approved?',
    description: 'True when the board purchase has been approved.',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  approved!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'Is Primary?',
    description: 'True when the board purchase is the primary',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  isPrimary!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'Payable?',
    description: 'True when the board purchase has been isPayable.',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  isPayable!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'isHighPriority?',
    description: 'True when the board is HighPriority.',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  isHighPriority!: boolean;

  @ApiProperty({
    type: () => GeographyPointDto,
    nullable: true,
    default: null,
    title: 'Approved Geo',
    description: 'GPS location of device when the board purchase was approved.',
  })
  @ValidateNested()
  @Type(() => GeographyPointDto)
  @Expose()
  approvedGeo!: GeographyPointDto | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'uuid',
    title: 'Associated Approving User ID',
  })
  @IsUUID()
  @Expose()
  approvingUserId!: string | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'uuid',
    readOnly: true,
    title: 'Creating User ID',
    description:
      'The user who created the board purchase. Set by the server on creation.',
  })
  @IsUUID()
  @Expose()
  createdByUserId!: string | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'Date Approved',
    description:
      'The date and time the board purchase was approved as complete',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateApproved!: Date | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'Date Promissed',
    description: 'The date and time the board purchase was promissed',
  })
  @IsISO8601({ strict: true })
  @Expose()
  datePromised!: Date | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'Date Delivery',
    description: 'The date and time the board purchase was delivered',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateDelivery!: Date | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    title: 'Invoice Number',
    description: 'Invoice Number',
  })
  @IsString()
  @Expose()
  invoiceNumber!: string | null;

  @ApiProperty({
    format: 'uuid',
    title: 'Supplier ID',
    description: 'The ID of the supplier for this job.',
  })
  @IsUUID()
  @Expose()
  supplierId!: string;

  @ApiPropertyOptional({
    type: () => SupplierDto,
    title: 'Associated Supplier',
    description:
      'The supplier associated with this job. ' +
      'No supplier is allowed if labor only is true.',
  })
  @Expose()
  supplier?: SupplierDto;

  @ApiProperty({
    format: 'uuid',
    title: 'Job ID',
    description: 'The ID of the job',
  })
  @IsUUID()
  @Expose()
  jobId!: string;

  @ApiPropertyOptional({
    type: () => JobDto,
    title: 'Associated Job',
    description: 'The job associated with this board purchase',
  })
  @Expose()
  job?: JobDto;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'Canceled?',
    description: 'True when the board purchase has been canceled.',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  canceled!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'Canceled Notification?',
    description: 'True should send cancelation emails',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  cancelNotification!: boolean;

  @ApiProperty({
    type: 'number',
    default: 1,
    title: 'icalSequence',
    description: 'Calendar email sequence',
  })
  @IsNumber()
  icalSequence!: number;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'Date Canceled',
    description: 'The date and time the board purchase was canceled',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateCanceled!: Date | null;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'StoppedFulfillment',
    description: 'True when Stopped Fulfillment.',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  stoppedFulfillment!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'StoppedFulfillment Notification?',
    description: 'True when should send a email',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  stoppedFulfillmentNotification!: boolean;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'Date Stopped Fulfillment',
    description: 'The date and time of Stopped fulfillment',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateStoppedFulfillment!: Date | null;

  @ApiPropertyOptional({
    type: () => [BoardPurchaseFloorDto],
    title: 'Associated Board Purchase Floors',
    description: 'The floors associated with this board purchase',
  })
  @Expose()
  boardPurchaseFloors?: BoardPurchaseFloorDto[];

  @ApiPropertyOptional({
    type: () => [BoardFloorDto],
    title: 'Associated Board Receipt Floors',
    description:
      'The floor boards received associated with this board purchase',
  })
  @Expose()
  boardReceiptFloors?: BoardFloorDto[];

  @ApiPropertyOptional({
    type: () => [MaterialDto],
    title: 'Associated Materials',
    description: 'The materials associated with this purchase',
  })
  @Expose()
  materials?: MaterialDto[];
}
